// src/pages/StagePage.js
import { collection, onSnapshot, query, where } from "firebase/firestore";
import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useAuth } from "../common/contexts/AuthContext";
import MessageLayout from "../components/Layout/MessageLayout";
import PasswordProtection from "../components/Stage/PasswordProtection";
import ScheduleCountdown from "../components/Stage/ScheduleCountdown";
import StageNavBar from "../components/Stage/StageNavBar";
import { useViewerAnalytics } from "../analytics/hooks/useViewerAnalytics";
import ChatSidebar from "../chat/components/ChatSidebar";
import { firestore } from "../firebase";
import VideoPlayer, { getPlaybackSignal } from "../generic/components/VideoPlayer";
import { getScheduleStartDate, isSchedulePending } from "../utils/stageSchedule";

export default function StagePage({ previewMode = false }) {
  const { slug } = useParams();
  const [isEventLoaded, setIsEventLoaded] = useState(false);
  const [eventData, setEventData] = useState(null);
  const [passwordError, setPasswordError] = useState("");
  const [isAuthorised, setIsAuthorised] = useState(false);
  const [scheduleGateTick, setScheduleGateTick] = useState(0);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const { currentUser } = useAuth();

  useEffect(() => {
    setIsEventLoaded(false);
    setEventData(null);
    setPasswordError("");
    setIsAuthorised(false);
    setIsVideoPlaying(false);
    setScheduleGateTick((tick) => tick + 1);

    if (previewMode && !currentUser) {
      setIsEventLoaded(true);
      return undefined;
    }

    const eventQuery = previewMode
      ? query(
          collection(firestore, "events"),
          where("custom_slug", "==", slug),
          where("user_id", "==", currentUser.uid)
        )
      : query(
          collection(firestore, "events"),
          where("custom_slug", "==", slug),
          where("is_public", "==", true)
        );

    const unsubscribe = onSnapshot(
      eventQuery,
      (snapshot) => {
        if (snapshot.empty) {
          setEventData(null);
          setIsVideoPlaying(false);
          setIsEventLoaded(true);
          return;
        }

        const eventDoc = snapshot.docs[0];
        const data = { ...eventDoc.data(), id: eventDoc.id };
        setEventData(data);
        setIsVideoPlaying(false);
        setIsAuthorised((wasAuthorised) => wasAuthorised || !data.password_protection);
        setIsEventLoaded(true);
      },
      (error) => {
        console.error("STAGE_FETCH_FAILED", error);
        setEventData(null);
        setIsVideoPlaying(false);
        setIsEventLoaded(true);
      }
    );

    return () => unsubscribe();
  }, [slug, previewMode, currentUser]);

  useEffect(() => {
    if (eventData?.event_name) document.title = eventData.event_name;
  }, [eventData]);

  useEffect(() => {
    if (!eventData?.header_code) return undefined;

    const script = document.createElement("script");
    script.innerHTML = eventData.header_code;
    document.head.appendChild(script);
    return () => document.head.removeChild(script);
  }, [eventData]);

  const scheduleStartDate = eventData ? getScheduleStartDate(eventData.schedule) : null;
  const shouldShowCountdown = eventData
    ? isSchedulePending(eventData.schedule, new Date())
    : false;
  const shouldShowPasswordGate = Boolean(
    eventData && !isAuthorised && eventData.password_protection
  );
  const shouldShowPasswordOnTimerCard = Boolean(
    shouldShowPasswordGate && eventData?.schedule?.enabled && scheduleStartDate
  );

  const handlePlaybackStateChange = useCallback(
    (nextIsPlaying) => {
      const normalizedIsPlaying = Boolean(nextIsPlaying);
      console.info(
        "PLAYER_PLAYBACK_STATE_CHANGED",
        `page_id=${eventData?.id || "unknown"} is_playing=${normalizedIsPlaying}`
      );
      setIsVideoPlaying(normalizedIsPlaying);
    },
    [eventData?.id]
  );

  useViewerAnalytics({
    pageId: eventData?.id,
    enabled: !previewMode && Boolean(eventData?.id),
    isPlaying: isVideoPlaying,
    stage: shouldShowCountdown || shouldShowPasswordGate ? "gate" : "player",
    playbackSignal: eventData?.video_url ? getPlaybackSignal(eventData.video_url) : undefined,
  });

  useEffect(() => {
    if (shouldShowCountdown || shouldShowPasswordGate) setIsVideoPlaying(false);
  }, [shouldShowCountdown, shouldShowPasswordGate]);

  function checkPassword(passwordInput) {
    if (passwordInput === eventData.password) {
      setIsAuthorised(true);
      setPasswordError("");
    } else {
      setPasswordError("Incorrect password");
    }
  }

  if (!isEventLoaded) {
    return <MessageLayout message="Loading page" type="loading" />;
  }

  if (!eventData && isEventLoaded) {
    return <MessageLayout message="Video not found" type="error" />;
  }

  return (
    <div
      id="stage-page"
      className="flex min-h-[100dvh] flex-col"
      style={{ backgroundColor: eventData.colors?.background || "#ffffff" }}
    >
      <StageNavBar stage={eventData} />

      {previewMode && (
        <div
          id="preview-mode"
          title="Preview Mode - This bar will not show in public views"
        >
          Preview Mode (analytics tracking is disabled here){" "}
          {eventData.is_public
            ? `- Publicly viewable at /s/${eventData.custom_slug}`
            : "- Not publicly viewable"}
        </div>
      )}

      <div className="flex min-h-0 flex-1 flex-col lg:flex-row">
        <main id="main-content" className="flex min-h-0 flex-1 items-center justify-center">
        {shouldShowCountdown ? (
          <ScheduleCountdown
            key={scheduleGateTick}
            stage={eventData}
            onComplete={() => setScheduleGateTick((tick) => tick + 1)}
          />
        ) : shouldShowPasswordOnTimerCard ? (
          <ScheduleCountdown
            stage={eventData}
            completeContent={
              <PasswordProtection
                checkPassword={checkPassword}
                passwordError={passwordError}
                variant="embedded"
              />
            }
          />
        ) : shouldShowPasswordGate ? (
          <PasswordProtection
            checkPassword={checkPassword}
            passwordError={passwordError}
          />
        ) : (
          <VideoPlayer
            video_url={eventData.video_url}
            onPlaybackStateChange={handlePlaybackStateChange}
          />
        )}
        </main>

        {!previewMode && eventData.chat?.enabled && <ChatSidebar stage={eventData} />}
      </div>
    </div>
  );
}
